import { coerceBooleanProperty } from '@angular/cdk/coercion'
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import {
  ApiService,
  AutocompleteQuestion,
  BooleanQuestion,
  Clinician,
  ClinicService,
  dropdownCompareFn,
  DropdownQuestion,
  Eligibility,
  filterAction,
  IDropdownOtherFieldType,
  IDropdownOtherFieldValue,
  Insurance,
  IntakeService,
  isOtherOptionValue,
  otherFieldValidator,
  PhoneQuestion,
  ProgressIndicatorService,
  QuestionBase,
  QuestionWithChildren,
  RadioQuestion,
  SelectOption,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { each, find, get, has, isNil, size } from 'lodash'
import moment from 'moment-timezone'
import { Observable, Subject, timer } from 'rxjs'
import {
  debounce,
  debounceTime,
  filter,
  map,
  startWith,
  takeUntil,
} from 'rxjs/operators'

import { OverrideIntakeComponent } from '../../dialogs/override-intake/override-intake.component'
import { TermsDialogComponent } from '../../dialogs/terms-dialog/terms-dialog.component'

@Component({
  selector: 'mmx-question',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss'],
  standalone: false,
})
export class QuestionComponent
implements AfterContentChecked, OnInit, AfterViewInit, OnDestroy
{
  @Input() question: QuestionBase<any>
  @Input() form: UntypedFormGroup
  @Input() parent: any
  @Input() disabled: boolean = false

  @ViewChild('otherFieldInput') otherFieldInput: ElementRef

  filteredOptions: Observable<SelectOption<string>[]>
  allowUnknownInsurance = false
  insuranceSuggestion: string
  isPulling: boolean
  clinicians: Clinician[]
  eligibility: Eligibility
  oldValue: any
  showNote = false
  hasTechModule = false
  formGroup: UntypedFormGroup
  otherFieldControl = new FormControl('')
  otherFieldVisible$: Observable<boolean>
  dropdownCompareFn = dropdownCompareFn
  countyFieldEnabled = this.clinicService.clinic.modules.extendedAddress || false

  private _onDestroy = new Subject<void>()

  constructor(
    private dialog: MatDialog,
    private el: ElementRef,
    private restService: ApiService,
    private intakeService: IntakeService,
    private ws: WebSocketService,
    private progressService: ProgressIndicatorService,
    private cdRef: ChangeDetectorRef,
    private clinicService: ClinicService,
  ) {}

  get clinicianName(): string | void {
    const clinician: Clinician = find(
      this.clinicians,
      (c) => c.id === this.question.clinicianId,
    )
    if (clinician) {
      return clinician.name
    }
  }

  get showAddNote(): boolean {
    return this.hasTechModule && this.question.isCustom
  }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges()
  }

  ngOnInit() {
    this.hasTechModule = !!this.clinicService.clinic.modules.tech
    this.formGroup = this.form.controls[
      this.question.isCustom ? this.question.id : this.question.key
    ] as UntypedFormGroup
    // Clean validation
    if (
      this.question.required &&
      this.question.key &&
      this.form.controls[this.question.key] &&
      isNil(this.form.value[this.question.key].value)
    ) {
      this.form.controls[this.question.key].reset()
    }

    if (
      this.question instanceof BooleanQuestion &&
      !isNil(this.question.value)
    ) {
      const value = coerceBooleanProperty(this.question.value)
      if (this.question.value !== value) {
        this.question.value = value
      }

      this.updateIfQuestions(this.question)
    } else if (
      this.question instanceof BooleanQuestion &&
      isNil(this.question.value)
    ) {
      // While is null skip child question validation
      this.updateIfQuestions(this.question)
    }

    if (
      (this.question instanceof DropdownQuestion ||
        this.question instanceof RadioQuestion) &&
      !isNil(this.question.value)
    ) {
      this.updateIfQuestions(this.question)
    }

    if (
      this.question instanceof DropdownQuestion &&
      this.question.hasOtherOption()
    ) {
      this.setupOtherFieldControl()
    }

    if (this.question instanceof AutocompleteQuestion) {
      this.filteredOptions = this.question.getOptions()
      this.question.control.valueChanges
        .pipe(debounceTime(250))
        .subscribe((val) => {
          if (this.question instanceof AutocompleteQuestion) {
            this.filteredOptions = this.question.getOptions(val)
          }
        })
    }

    if (has(this.question, 'options.allowUnknownInsurance')) {
      if (this.intakeService.data.allowUnknownInsurance === true) {
        this.form.get(this.question.key).setValidators([])
        this.form.get(this.question.key).updateValueAndValidity()
        this.form.get(this.question.key).disable()
        this.allowUnknownInsurance = true
      }
      this.form
        .get('insuranceCarrier')
        .valueChanges.pipe(debounce(() => timer(1000)))
        .subscribe(() => {
          if (
            this.form.get('hasInsurance') &&
            this.form.get('hasInsurance').value
          ) {
            this.fetchInsurance()
          }
        })

      this.form
        .get('insuranceNumber')
        .valueChanges.pipe(debounce(() => timer(1000)))
        .subscribe(() => {
          if (
            this.form.get('hasInsurance') &&
            this.form.get('hasInsurance').value
          ) {
            this.fetchInsurance()
          }
        })

      this.eligibility = this.intakeService.eligibility
    }

    this.clinicians = this.intakeService.clinicians

    if (this.question.isCustom) {
      this.form.get(this.question.id).valueChanges.subscribe((newValue) => {
        if (this.oldValue === undefined) {
          this.oldValue = this.form.value[this.question.id].value
        }

        if (newValue.value !== this.oldValue) {
          this.showNote = true
        } else {
          this.showNote = false
        }
      })
    }

    if (this.disabled) {
      requestAnimationFrame(() => {
        Object.keys(this.formGroup.controls).forEach((key) => {
          this.formGroup.controls[key].disable()
        })
      })
    }
  }

  ngAfterViewInit() {
    const ele = this.el.nativeElement.querySelector('input[type=number]')
    if (ele) {
      ele.addEventListener('keypress', (e: any) => {
        const invalidChars = ['-', '+', 'e']
        if (invalidChars.includes(e.key)) {
          e.preventDefault()
        }
      })
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next()
    this._onDestroy.complete()
  }

  get isValid() {
    return this.question.valid
  }

  get showQuestions() {
    if (this.question.parent) {
      if (
        this.form.contains(this.question.parent) &&
        this.form.get(this.question.parent).valid
      ) {
        return true
      } else if (
        !this.form.contains(this.question.parent) &&
        this.question.getParentQuestionValue(
          this.intakeService.data,
          this.question.parent,
        )
      ) {
        return true
      } else {
        // remove validator if any
        if (this.form.contains(this.question.id)) {
          this.form.get(this.question.id).setValidators(null)
          this.form.get(this.question.id).setErrors(null)
          this.form.get(this.question.id).updateValueAndValidity()
        }
        if (this.form.contains(this.question.key)) {
          this.form.get(this.question.key).setValidators(null)
          this.form.get(this.question.key).setErrors(null)
          this.form.get(this.question.key).updateValueAndValidity()
        }
        return false
      }
    } else {
      return true
    }
  }

  get showIfQuestions() {
    const question = this.question as QuestionWithChildren
    return question.showIfQuestions
  }

  onAllowUnknownInsuranceChange() {
    this.intakeService.data.allowUnknownInsurance = this.allowUnknownInsurance
    if (this.allowUnknownInsurance) {
      this.form.get(this.question.key).setValidators([])
      this.form.get(this.question.key).updateValueAndValidity()
      this.form.get(this.question.key).disable()
      this.form.get(this.question.key).setValue(undefined)
    } else {
      this.form
        .get(this.question.key)
        .setValidators(this.question.getValidators())
      this.form.get(this.question.key).updateValueAndValidity()
      this.form.get(this.question.key).enable()
    }
  }

  getErrorMessage(controlName?: string): string {
    return this.intakeService.getErrorMessage(controlName, this.formGroup)
  }

  handleInsuranceDetail(data: Insurance): void {
    if (data.key === 'insuranceCard') {
      this.form.controls['insuranceCarrier'].setValue(data.insuranceCarrier)
      this.form.controls['insuranceNumber'].setValue(data.insuranceNumber)
    } else if (data.key === 'secondaryInsuranceCard') {
      this.form.controls['secondaryInsuranceCarrier'].setValue(
        data.insuranceCarrier,
      )
      this.form.controls['secondaryInsuranceNumber'].setValue(
        data.insuranceNumber,
      )
    }
  }

  onSelectChange(value: {
    value: string | IDropdownOtherFieldValue
    note: string | null
  }) {
    if (
      (this.question as DropdownQuestion).hasOtherOption() &&
      isOtherOptionValue(value.value)
    ) {
      const control = this.form.controls[this.question.id]
      control.setValidators([
        ...(this.question.validators || []),
        otherFieldValidator(this.otherFieldControl),
      ])
      this.otherFieldControl.setValidators([Validators.required])
      control.updateValueAndValidity()

      setTimeout(() => {
        this.otherFieldInput?.nativeElement?.focus()
      }, 10)
    } else {
      this.question.value = value
    }

    this.updateIfQuestions(this.question as DropdownQuestion)
  }

  onRadioChange() {
    this.question.value = this.formGroup.value.value
    this.updateIfQuestions(this.question as RadioQuestion)
  }

  onBoolChange() {
    const question = this.question as BooleanQuestion
    const value = this.formGroup.value.value
    this.question.value = coerceBooleanProperty(value)
    this.updateIfQuestions(question)
  }
  /**this.form.get
   * Save drawn image
   * @param imageData Data URL Base64-encoded string of image
   */
  onDraw(imageData: string) {
    const drawControl = (this.form.get(this.question.id) as UntypedFormGroup)
      .controls['value']
    drawControl.setValue(imageData)
  }

  openTerms() {
    const dialogRef = this.dialog.open(TermsDialogComponent, {
      data: {
        acceptable: true,
      },
    })

    dialogRef.afterClosed().subscribe((accepted: boolean) => {
      if (accepted && this.question instanceof PhoneQuestion) {
        this.form.controls[this.question.subscribeSmsKey].setValue(accepted)
      }
    })
  }

  openNoteDialog() {
    const overrideDialog = this.dialog.open(OverrideIntakeComponent, {
      panelClass: 'dialog-form',
      autoFocus: false,
      width: '600px',
      data: this.question,
    })

    overrideDialog.afterClosed().subscribe((updatedField) => {
      if (updatedField) {
        this.formGroup.controls['note'].setValue(updatedField.note)
      }
    })
  }

  private updateIfQuestions(question: QuestionWithChildren) {
    // update the ifQuestion validators for being required
    if (size(question.ifQuestions) > 0) {
      const show = question.showIfQuestions
      each(question.ifQuestions, (q) => {
        const control: UntypedFormControl | UntypedFormGroup = q.isCustom
          ? (this.form.controls[q.id] as any)
          : (this.form.controls[q.key] as any)
        if ((q.key && q.required) || (q.id && q.required)) {
          if (control instanceof UntypedFormControl) {
          } else {
            each(control.controls, (controlItem, key) => {
              if (show && key !== 'address2' && key !== 'address3') {
                if (key === 'city') {
                  const validators = question.getValidators()
                  validators.push(Validators.pattern(/^[^0-9]*$/))
                  controlItem.setValidators(validators)
                } else if (key === 'value') {
                  controlItem.setValidators(q.getValidators())
                }
              } else {
                controlItem.clearValidators()
              }
              controlItem.updateValueAndValidity()
              controlItem.setValue(controlItem.value)
              controlItem.markAsPristine()
              controlItem.markAsUntouched()
            })
          }
        }

        if (question.ifQuestions && size(question.ifQuestions) > 0) {
          this.updateIfQuestions(q as QuestionWithChildren)
        }
      })
    }
  }

  private fetchInsurance() {
    const insuranceProvider: string =
      this.form.get('insuranceCarrier').value.value
    const insuranceNumber: string = this.form.get('insuranceNumber').value.value

    if (insuranceProvider && insuranceNumber && insuranceNumber.length > 8) {
      this.isPulling = true

      const body: any = {
        patient: this.intakeService.data.patientId,
        appointment: this.intakeService.data.appointmentId,
        memberId: insuranceNumber,
        carrier: insuranceProvider,
        dob: moment(this.intakeService.data.dob, moment.HTML5_FMT.DATE).format(
          'YYYY-MM-DD',
        ),
        sex: this.intakeService.data.sex,
      }

      this.ws.clinicianMessage$
        .pipe(filterAction(WebsocketAction.ELIGIBILITY_INQUIRY_CONSOLE))
        .subscribe({
          next: (response) => {
            this.eligibility = new Eligibility(response.data)

            if (
              this.eligibility.data.errors &&
              this.eligibility.data.errors.length > 0
            ) {
              const errorMessage = this.eligibility.data.errors
                .map((e) => e.message)
                .join(', ')
              this.progressService.snack(errorMessage)
            }
            this.isPulling = false
          },
          error: (err) => {
            const errorMessage =
              get(err, 'error.errors[0].message') ||
              get(err, 'error.message') ||
              'An unknown error occurred'
            this.progressService.snack(errorMessage)
            this.isPulling = false
          },
        })

      this.ws.messageQueue.next({
        action: WebsocketAction.ELIGIBILITY_INQUIRY_CONSOLE,
        ...body,
      })
    }
  }

  private setupOtherFieldControl() {
    const control = this.form.controls[this.question.id]

    this.otherFieldVisible$ = control?.valueChanges.pipe(
      startWith(''),
      map(() => isOtherOptionValue(control.value.value)),
    )

    if (control?.value?.value) {
      const selectValue = control.value.value
      if (selectValue.type === IDropdownOtherFieldType) {
        this.otherFieldControl.setValue(selectValue.value)
      }
      this.onSelectChange(control.value)
    }

    this.otherFieldControl.valueChanges
      .pipe(
        startWith(control.value?.value ?? ''),
        takeUntil(this._onDestroy),
        filter(() => control.value.value?.type === IDropdownOtherFieldType),
      )
      .subscribe((value) => {
        this.question.value = {
          type: IDropdownOtherFieldType,
          value,
        }
        control.updateValueAndValidity()
      })
  }
}
